import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './CellEditor.scss';
import { Button, IconButton, TextField, InputAdornment } from '@material-ui/core';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import CellToolbar from './CellToolbar';
import CellCanvas from './CellCanvas';
import {
  CELL_TYPES,
  createCell,
  createEmptySchema,
  addCell,
  updateCell,
  removeCell,
  bringToFront,
  sendToBack,
  duplicateCell,
  getCellById,
} from './cellSchema';
import { htmlToCells, isEmptyHtml } from './htmlToCells';
import { cellsToHtml, cellsToTableHtml, cellsToTriggerHtml } from './cellsToHtml';

import componentsIcon from '../../../../../../assets/img/Components.svg';
import bodyIcon from '../../../../../../assets/img/Body.svg';
import textCellCompImg from '../../../../../../assets/img/TextCellComponent.svg';
import buttonCompImg from '../../../../../../assets/img/ButtonComponent.svg';
import dividerCompImg from '../../../../../../assets/img/DividerComponent.svg';
import socialCompImg from '../../../../../../assets/img/SocialComponent.svg';
import imageCompImg from '../../../../../../assets/img/imageComponent.svg';
import tableCompImg from '../../../../../../assets/img/TableComponent.svg';
import loopCompImg from '../../../../../../assets/img/Loop.svg';
import {
  createTableModel,
  cloneModel as cloneTableModel,
  tableModelToHtml,
  TableGridSelector,
  TableRightPanel,
} from '../email-table/TableComponent';
import alignLeftIcon from '../../../../../../assets/img/Left.svg';
import alignCenterIcon from '../../../../../../assets/img/Center.svg';
import alignRightIcon from '../../../../../../assets/img/Right.svg';
import thinLine from '../../../../../../assets/img/Line1.svg';
import midLine from '../../../../../../assets/img/Line2.svg';
import thickLine from '../../../../../../assets/img/Line3.svg';
import sortIcon from '../../../../../../assets/img/SortMini.svg'; 

import CustomColorPicker from '../../../../../../shared/form-controls/custom-color-picker/CustomColorPicker';
import CustomTextField from '../../../../../../shared/form-controls/custom-text-field/Custom-Text-Field';
import { isAllowedExternalUrl } from '../../../../../../utils/urlValidationUtils';
import VariableKeywordsModal from '../../../../welcome-message/message-create-view/VariableKeywordsModal';
import { getEmailTemplateVariables } from '../../../../../../utils/ServicesAPIUtils';

const EmailVariableKeywordsModal = (props) => {
  const customFetcher = useCallback(() => getEmailTemplateVariables('variables'), []);
  return <VariableKeywordsModal {...props} customFetcher={customFetcher} />;
};

const THICKNESS_ICON_MAP = {
  1: thinLine,
  2: midLine,
  3: thickLine,
};

const DEFAULTS = {
  button: {
    text: 'Button',
    url: '',
    bg: '#7a6a55',
    color: '#ffffff',
    align: 'center',
    radius: 10,
    borderColor: 'transparent',
  },
  divider: { color: '#7a6a55', thickness: 2 },
  social: {
    align: 'center',
    linkedin: '',
    facebook: '',
    twitter: '',
    instagram: '',
    youtube: '',
    iconColor: '#EED191',
    spacing: 10,
    order: ['linkedin', 'facebook', 'twitter', 'instagram', 'youtube'],
  },
};

const VariableDropdown = React.forwardRef(({ position, variables, onSelect }, ref) => {
  if (!variables.length) return null;

  return (
    <div
      ref={ref}
      className='variable-dropdown'
      style={{
        top: position?.y,
        left: position.x,
      }}
    >
      <div className='variable-header'>Variables</div>
      {variables.map((variable, index) => (
        <div
          key={index}
          className='variable-item'
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect(variable);
          }}
        >
          {variable.keyword}
        </div>
      ))}
    </div>
  );
});

const CellEditor = ({
  value = {},
  onChange,
  formData,
  handleInputChange,
  isReadOnly = false,
  canvasFooter,
  registerHistoryApi,
}) => {
  const initialSchema = useMemo(() => {
    const html = value?.html || '';
    if (isEmptyHtml(html)) {
      const empty = createEmptySchema();
      empty.bgColor = value?.bgColor || '#ffffff';
      return empty;
    }
    const parsed = htmlToCells(html);
    if (value?.bgColor) {
      parsed.bgColor = value.bgColor;
    }
    return parsed;
  }, []);

  const [schema, setSchema] = useState(initialSchema);
  const [selectedCellId, setSelectedCellId] = useState(null);
  const [activeTab, setActiveTab] = useState('components');
  const [showVariableList, setShowVariableList] = useState(false);

  const [dragKey, setDragKey] = useState(null);
  const [showTableGridPicker, setShowTableGridPicker] = useState(false);
  const [socialUrlErrors, setSocialUrlErrors] = useState({});
  const [buttonUrlError, setButtonUrlError] = useState(null);
  const [imageUrlError, setImageUrlError] = useState(null);
  const [redirectUrlError, setRedirectUrlError] = useState(null);

  const [showVariableDropdown, setShowVariableDropdown] = useState(false);
  const [variableDropdownPosition, setVariableDropdownPosition] = useState({ x: 0, y: 0 });
  const [variables, setVariables] = useState([]);
  const [selectedCellForVar, setSelectedCellForVar] = useState(null);

  const [history, setHistory] = useState([initialSchema]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const historyRef = useRef({ history: [initialSchema], index: 0 });
  const variableDropdownRef = useRef(null);
  const currentContentRef = useRef('');
  const loopVarCondIdxRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const HISTORY_LIMIT = 500;

  useEffect(() => {
    historyRef.current = { history, index: historyIndex };
  }, [history, historyIndex]);

  const isValidUrl = (string) => {
    if (!string || string.trim() === '') return false;
    try {
      const url = new URL(string);
      if (url.protocol !== 'https:') return false;
      if (!isAllowedExternalUrl(string)) return false;
      return true;
    } catch {
      return false;
    }
  };

  const validateCell = useCallback((cell) => {
    if (cell.type === CELL_TYPES.SOCIAL) {
      const links = cell.socialLinks || {};
      const order = cell.socialOrder || [
        'linkedin',
        'facebook',
        'twitter',
        'instagram',
        'youtube',
      ];
      let hasValid = false;
      let hasInvalid = false;
      for (const key of order) {
        const val = links[key];
        if (val && val.trim() !== '') {
          if (isValidUrl(val)) {
            hasValid = true;
          } else {
            hasInvalid = true;
          }
        }
      }
      return hasValid && !hasInvalid;
    }
    if (cell.type === CELL_TYPES.BUTTON) {
      return isValidUrl(cell.buttonUrl);
    }
    if (cell.type === CELL_TYPES.IMAGE) {
      return (
        cell.imageUrl &&
        cell.imageUrl.trim() !== '' &&
        isValidUrl(cell.imageUrl) &&
        !cell.isImageBroken
      );
    }
    if (cell.type === CELL_TYPES.TEXT) {
      const content = (cell.content || '').trim();
      const plainText = content.replace(/<[^>]*>?/gm, '').trim();
      const isPlaceholderText = plainText === 'Double Click to Enter Text...';
      return plainText !== '' && !isPlaceholderText;
    }
    if (cell.type === CELL_TYPES.LOOP) {
      const conds = cell.loopConditions || [];
      return conds.length > 0;
    }
    return true;
  }, []);

  const isValidSchema = useMemo(() => {
    if (schema.cells.length === 0) return true;
    return schema.cells.every((cell) => validateCell(cell));
  }, [schema, validateCell]);

  const isEmpty = useMemo(() => schema.cells.length === 0, [schema]);

  useEffect(() => {
    if (selectedCell && selectedCell.type === CELL_TYPES.BUTTON) {
      const isValid = isValidUrl(selectedCell.buttonUrl);
      setButtonUrlError(isValid ? null : 'Invalid URL');
    } else {
      setButtonUrlError(null);
    }

    if (selectedCell && selectedCell.type === CELL_TYPES.IMAGE) {
      const isValid = !selectedCell.imageUrl || isValidUrl(selectedCell.imageUrl);
      setImageUrlError(isValid ? null : 'Invalid URL');
      const isRedirectValid = !selectedCell.redirectUrl || isValidUrl(selectedCell.redirectUrl);
      setRedirectUrlError(isRedirectValid ? null : 'Invalid URL');
    } else {
      setImageUrlError(null);
      setRedirectUrlError(null);
    }
  }, [selectedCellId]);

  useEffect(() => {
    const html = cellsToHtml(schema);
    const tableHtml = cellsToTableHtml(schema);
    const triggerHtml = cellsToTriggerHtml(schema);
    const payload = { html, tableHtml, triggerHtml, bgColor: schema.bgColor, isValid: isValidSchema, isEmpty };
    onChange && onChange(payload);

    if (handleInputChange && formData) {
      handleInputChange({
        emailCustomTemplate: {
          ...(formData?.emailCustomTemplate || {
            fieldName: 'Custom Email Template',
            validations: [],
            errors: {},
            tab: 1,
          }),
          value: payload,
          errors: {},
        },
      });
    }
  }, [schema, isValidSchema, isEmpty]);

  const selectedCell = useMemo(
    () => (selectedCellId ? getCellById(schema, selectedCellId) : null),
    [schema, selectedCellId],
  );

  const pushHistory = useCallback((newSchema) => {
    setHistory((prev) => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(newSchema);
      
      if (newHistory.length > HISTORY_LIMIT) {
        const removeCount = Math.floor(HISTORY_LIMIT * 0.2);
        newHistory.splice(1, removeCount);
        return newHistory;
      }
      
      return newHistory;
    });
    setHistoryIndex((prev) => {
      const newIndex = prev + 1;
      if (newIndex > HISTORY_LIMIT) {
        const removeCount = Math.floor(HISTORY_LIMIT * 0.2);
        return newIndex - removeCount;
      }
      return newIndex;
    });
  }, [historyIndex, HISTORY_LIMIT]);

  const updateSchemaWithHistory = useCallback(
    (newSchema) => {
      setSchema(newSchema);
      pushHistory(newSchema);
    },
    [pushHistory],
  );

  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setSchema(history[newIndex]);
    }
  }, [history, historyIndex]);

  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setSchema(history[newIndex]);
    }
  }, [history, historyIndex]);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  useEffect(() => {
    registerHistoryApi &&
      registerHistoryApi({
        undo,
        redo,
        canUndo: () => canUndo,
        canRedo: () => canRedo,
      });
  }, [registerHistoryApi, undo, redo, canUndo, canRedo]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toLowerCase().includes('mac');
      const mod = isMac ? e.metaKey : e.ctrlKey;

      if (mod && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (
        mod &&
        (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))
      ) {
        e.preventDefault();
        redo();
      } else if (e.key === 'Delete' && selectedCellId) {
        e.preventDefault();
        handleDeleteCell();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, selectedCellId]);

  useEffect(() => {
    setShowVariableDropdown(false);
  }, [selectedCellId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (variableDropdownRef.current && !variableDropdownRef.current.contains(event.target)) {
        setShowVariableDropdown(false);
      }
    };

    if (showVariableDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showVariableDropdown]);

  const handleAddCell = useCallback(
    (type) => {
      if (isReadOnly) return;

      const scrollY = scrollContainerRef.current?.scrollTop || 0;
      const newCell = createCell(type, {
        x: 50 + Math.random() * 100,
        y: scrollY + 50 + Math.random() * 100,
      });

      const newSchema = addCell(schema, newCell);
      updateSchemaWithHistory(newSchema);
      setSelectedCellId(newCell.id);
    },
    [schema, updateSchemaWithHistory, isReadOnly],
  );

  const handleInsertTable = useCallback(
    (rows, cols) => {
      if (isReadOnly) return;
      const scrollY = scrollContainerRef.current?.scrollTop || 0;
      const tModel = createTableModel(rows, cols);
      const newCell = createCell(CELL_TYPES.TABLE, {
        x: 50,
        y: scrollY + 50,
        width: 500,
        height: Math.max(200, rows * 50 + 40),
        tableModel: tModel,
      });
      const newSchema = addCell(schema, newCell);
      updateSchemaWithHistory(newSchema);
      setSelectedCellId(newCell.id);
      setShowTableGridPicker(false);
    },
    [schema, updateSchemaWithHistory, isReadOnly],
  );

  const handleUpdateCell = useCallback(
    (cellId, updates) => {
      if (isReadOnly) return;
      let finalUpdates = updates;
      if (updates.imageUrl !== undefined) {
        finalUpdates = { ...updates, isImageBroken: false };
      }
      // When table model changes (rows/cols added), grow the table cell proportionally
      if (finalUpdates.tableModel) {
        const currentCell = schema.cells.find((c) => c.id === cellId);
        if (currentCell && currentCell.tableModel) {
          const oldRows = currentCell.tableModel.grid?.length || 0;
          const oldCols = currentCell.tableModel.grid?.[0]?.length || 0;
          const newRows = finalUpdates.tableModel.grid?.length || 0;
          const newCols = finalUpdates.tableModel.grid?.[0]?.length || 0;
          if (newCols > oldCols) {
            const newW = Math.round(currentCell.width * (newCols / oldCols));
            finalUpdates = { ...finalUpdates, width: newW };
          }
          if (newRows > oldRows) {
            const newH = Math.round(currentCell.height * (newRows / oldRows));
            finalUpdates = { ...finalUpdates, height: newH };
          }
          // Enforce minimum dimensions
          const tm = finalUpdates.tableModel;
          const numRows = tm.grid?.length || 0;
          const numCols = tm.grid?.[0]?.length || 0;
          const minH = Math.max(200, numRows * 40);
          const minW = Math.max(200, numCols * 80);
          const curH = finalUpdates.height || currentCell.height;
          const curW = finalUpdates.width || currentCell.width;
          if (curH < minH) finalUpdates = { ...finalUpdates, height: minH };
          if (curW < minW) finalUpdates = { ...finalUpdates, width: minW };
        }
      }
      let newSchema = updateCell(schema, cellId, finalUpdates);
      setSchema(newSchema);
    },
    [schema, isReadOnly],
  );

  const handleUpdateCellComplete = useCallback(
    (cellId, updates) => {
      if (isReadOnly) return;
      let finalUpdates = updates;
      if (updates.imageUrl !== undefined) {
        finalUpdates = { ...updates, isImageBroken: false };
      }
      // When table model changes (rows/cols added), grow the table cell proportionally
      if (finalUpdates.tableModel) {
        const currentCell = schema.cells.find((c) => c.id === cellId);
        if (currentCell && currentCell.tableModel) {
          const oldRows = currentCell.tableModel.grid?.length || 0;
          const oldCols = currentCell.tableModel.grid?.[0]?.length || 0;
          const newRows = finalUpdates.tableModel.grid?.length || 0;
          const newCols = finalUpdates.tableModel.grid?.[0]?.length || 0;
          if (newCols > oldCols) {
            const newW = Math.round(currentCell.width * (newCols / oldCols));
            finalUpdates = { ...finalUpdates, width: newW };
          }
          if (newRows > oldRows) {
            const newH = Math.round(currentCell.height * (newRows / oldRows));
            finalUpdates = { ...finalUpdates, height: newH };
          }
          // Enforce minimum dimensions
          const tm = finalUpdates.tableModel;
          const numRows = tm.grid?.length || 0;
          const numCols = tm.grid?.[0]?.length || 0;
          const minH = Math.max(200, numRows * 40);
          const minW = Math.max(200, numCols * 80);
          const curH = finalUpdates.height || currentCell.height;
          const curW = finalUpdates.width || currentCell.width;
          if (curH < minH) finalUpdates = { ...finalUpdates, height: minH };
          if (curW < minW) finalUpdates = { ...finalUpdates, width: minW };
        }
      }
      let newSchema = updateCell(schema, cellId, finalUpdates);
      updateSchemaWithHistory(newSchema);
    },
    [schema, updateSchemaWithHistory, isReadOnly],
  );

  const handleUpdateCellWithHistory = useCallback(
    (cellId, updates) => {
      if (isReadOnly) return;
      let finalUpdates = updates;
      if (updates.imageUrl !== undefined) {
        finalUpdates = { ...updates, isImageBroken: false };
      }
      // When table model changes (rows/cols added), grow the table cell proportionally
      if (finalUpdates.tableModel) {
        const currentCell = schema.cells.find((c) => c.id === cellId);
        if (currentCell && currentCell.tableModel) {
          const oldRows = currentCell.tableModel.grid?.length || 0;
          const oldCols = currentCell.tableModel.grid?.[0]?.length || 0;
          const newRows = finalUpdates.tableModel.grid?.length || 0;
          const newCols = finalUpdates.tableModel.grid?.[0]?.length || 0;
          if (newCols > oldCols) {
            const newW = Math.round(currentCell.width * (newCols / oldCols));
            finalUpdates = { ...finalUpdates, width: newW };
          }
          if (newRows > oldRows) {
            const newH = Math.round(currentCell.height * (newRows / oldRows));
            finalUpdates = { ...finalUpdates, height: newH };
          }
        }
      }
      const newSchema = updateCell(schema, cellId, finalUpdates);
      updateSchemaWithHistory(newSchema);
    },
    [schema, updateSchemaWithHistory, isReadOnly],
  );

  const handleDeleteCell = useCallback(() => {
    if (isReadOnly || !selectedCellId) return;
    const newSchema = removeCell(schema, selectedCellId);
    updateSchemaWithHistory(newSchema);
    setSelectedCellId(null);
    setShowVariableDropdown(false);
  }, [schema, selectedCellId, updateSchemaWithHistory, isReadOnly]);

  const handleDuplicateCell = useCallback(() => {
    if (isReadOnly || !selectedCellId) return;
    const newSchema = duplicateCell(schema, selectedCellId);
    updateSchemaWithHistory(newSchema);
    const newCellId = newSchema.cells[newSchema.cells.length - 1]?.id;
    if (newCellId) setSelectedCellId(newCellId);
  }, [schema, selectedCellId, updateSchemaWithHistory, isReadOnly]);

  const handleBringToFront = useCallback(() => {
    if (isReadOnly || !selectedCellId) return;
    const newSchema = bringToFront(schema, selectedCellId);
    updateSchemaWithHistory(newSchema);
  }, [schema, selectedCellId, updateSchemaWithHistory, isReadOnly]);

  const handleSendToBack = useCallback(() => {
    if (isReadOnly || !selectedCellId) return;
    const newSchema = sendToBack(schema, selectedCellId);
    updateSchemaWithHistory(newSchema);
  }, [schema, selectedCellId, updateSchemaWithHistory, isReadOnly]);

  const handleContentChange = useCallback(
    (cellId, content) => {
      if (isReadOnly) return;
      handleUpdateCellWithHistory(cellId, { content });
    },
    [handleUpdateCellWithHistory, isReadOnly],
  );

  const handleBgColorChange = useCallback(
    (color) => {
      if (isReadOnly) return;
      const newSchema = { ...schema, bgColor: color };
      updateSchemaWithHistory(newSchema);
    },
    [schema, updateSchemaWithHistory, isReadOnly],
  );

  const validateSocialUrl = (key, value) => {
    const isValid = value.trim() === '' || isValidUrl(value);
    setSocialUrlErrors((prev) => ({
      ...prev,
      [key]: isValid ? null : 'Invalid URL',
    }));
    return isValid;
  };

  const onDragStart = (e, type) => {
    e.dataTransfer.effectAllowed = 'copyMove';
    e.dataTransfer.setData('text/plain', type);
    e.dataTransfer.setData('component', type);
  };

  const quickInsert = (type) => {
    if (isReadOnly) return;
    handleAddCell(type);
  };

  const renderRightPanel = () => {
    if (selectedCell) {
      return renderCellEditPanel();
    }
    return renderDefaultPanel();
  };

  const renderCellEditPanel = () => {
    if (!selectedCell) return null;

    switch (selectedCell.type) {
      case CELL_TYPES.BUTTON:
        return renderButtonPanel();
      case CELL_TYPES.DIVIDER:
        return renderDividerPanel();
      case CELL_TYPES.SOCIAL:
        return renderSocialPanel();
      case CELL_TYPES.TEXT:
        return renderTextPanel();
      case CELL_TYPES.IMAGE:
        return renderImagePanel();
      case CELL_TYPES.TABLE:
        return renderTablePanel();
      case CELL_TYPES.LOOP:
        return renderLoopPanel();
      default:
        return renderDefaultPanel();
    }
  };

  const renderButtonPanel = () => {
    const c = selectedCell;
    const styles = c.styles || {};

    return (
      <>
        <div className='email-editor-header'>
          <h5 className='email-edit-text mt-4'>Edit Button</h5>
          <IconButton
            className='close-icon'
            style={{ color: 'white', marginRight: '1rem' }}
            onClick={() => setSelectedCellId(null)}
          >
            <CloseIcon className='close-icon' style={{ color: 'white' }} />
          </IconButton>
        </div>

        <div className='email-editor-panel'>
          <div className='text-color-edit'>
            <span className='text-color-label'>Button Color</span>
            <CustomColorPicker
              name='btnbg'
              value={styles.backgroundColor || '#7a6a55'}
              onChange={(e) => {
                handleUpdateCellWithHistory(c.id, {
                  styles: { ...styles, backgroundColor: e.target.value },
                });
              }}
            />
          </div>

          <div className='text-color-edit' style={{ marginTop: 16 }}>
            <span className='text-color-label'>Text Color</span>
            <CustomColorPicker
              name='btncolor'
              value={styles.color || '#ffffff'}
              onChange={(e) => {
                handleUpdateCellWithHistory(c.id, {
                  styles: { ...styles, color: e.target.value },
                });
              }}
            />
          </div>

          <div className='text-color-edit' style={{ marginTop: 16 }}>
            <span className='text-color-label'>Outline Color</span>
            <CustomColorPicker
              name='btnoutline'
              value={styles.borderColor || 'transparent'}
              onChange={(e) => {
                const newStyles = { ...styles, borderColor: e.target.value };
                handleUpdateCellWithHistory(c.id, {
                  styles: newStyles,
                });
              }}
            />
          </div>

          <hr className='horizontal-lines' />

          <div className='alignment-buttons' style={{ marginTop: 20 }}>
            <span className='alignment-label'>Alignment</span>
            <div className='align-toggle' role='group'>
              {['left', 'center', 'right'].map((opt) => (
                <button
                  key={opt}
                  type='button'
                  className={`align-btn-icon${
                    (styles.textAlign || 'center') === opt ? ' active' : ''
                  }`}
                  onClick={() => {
                    handleUpdateCellWithHistory(c.id, {
                      styles: { ...styles, textAlign: opt },
                    });
                  }}
                  title={opt}
                >
                  <img
                    src={
                      opt === 'left'
                        ? alignLeftIcon
                        : opt === 'center'
                        ? alignCenterIcon
                        : alignRightIcon
                    }
                    alt={opt}
                  />
                </button>
              ))}
            </div>
          </div>

          <hr className='horizontal-lines' />

          <div className='button-text-grid' style={{ marginTop: 35 }}>
            <CustomTextField
              className='button-text-grid-field'
              item={{
                fieldName: 'Add Button Text',
                value: c.content,
                validations: [],
                errors: {},
              }}
              type='text'
              placeholder='Enter button text'
              hideAdornmentByDefault={false}
              endAdornment={true}
              characterCount={true}
              maxLength={50}
              handleInputChange={(e) => {
                const val = (e.value || '').slice(0, 50);
                handleUpdateCellWithHistory(c.id, { content: val });
              }}
            />
          </div>

          <div className='button-text-grid' style={{ marginTop: 12 }}>
            <CustomTextField
              className={`button-text-grid-url${buttonUrlError ? ' has-error' : ''}`}
              item={{
                fieldName: 'Add Button URL',
                value: c.buttonUrl || '',
                validations: [],
                errors: {},
              }}
              type='text'
              placeholder='https://…'
              hideAdornmentByDefault={true}
              handleInputChange={(e) => {
                const val = e.value || '';
                const isValid = isValidUrl(val);
                setButtonUrlError(isValid ? null : 'Invalid URL');
                handleUpdateCellWithHistory(c.id, { buttonUrl: val });
              }}
            />
            {buttonUrlError && (
              <div className='social-url-error' style={{ marginTop: 4 }}>
                {buttonUrlError}
              </div>
            )}
          </div>

          <hr className='horizontal-lines' />

          <div style={{ display: 'flex', gap: 12, marginTop: 16, paddingBottom: 60 }}>
            <div style={{ flex: 1 }}>
              <CustomTextField
                item={{
                  fieldName: 'Width (px)',
                  value: c.buttonWidth === '' || c.buttonWidth === undefined || c.buttonWidth === null ? '' : c.buttonWidth,
                  validations: [],
                  errors: {},
                }}
                type='number'
                min={1}
                max={800}
                step={1}
                hideAdornmentByDefault={true}
                handleInputChange={(e) => {
                  const val = e.value;
                  if (val === '' || val === null) {
                    handleUpdateCellWithHistory(c.id, { buttonWidth: '' });
                    return;
                  }
                  const num = Number(val);
                  if (Number.isNaN(num)) {
                    handleUpdateCellWithHistory(c.id, { buttonWidth: '' });
                    return;
                  }
                  const clamped = Math.min(800, Math.max(1, Math.floor(num)));
                  handleUpdateCellWithHistory(c.id, { buttonWidth: clamped });
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <CustomTextField
                item={{
                  fieldName: 'Height (px)',
                  value: c.buttonHeight === '' || c.buttonHeight === undefined || c.buttonHeight === null ? '' : c.buttonHeight,
                  validations: [],
                  errors: {},
                }}
                type='number'
                min={1}
                max={150}
                step={1}
                hideAdornmentByDefault={true}
                handleInputChange={(e) => {
                  const val = e.value;
                  if (val === '' || val === null) {
                    handleUpdateCellWithHistory(c.id, { buttonHeight: '' });
                    return;
                  }
                  const num = Number(val);
                  if (Number.isNaN(num)) {
                    handleUpdateCellWithHistory(c.id, { buttonHeight: '' });
                    return;
                  }
                  const clamped = Math.min(150, Math.max(1, Math.floor(num)));
                  handleUpdateCellWithHistory(c.id, { buttonHeight: clamped });
                }}
              />
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderDividerPanel = () => {
    const c = selectedCell;

    const setThickness = (v) => {
      handleUpdateCellWithHistory(c.id, { dividerThickness: v });
    };

    const ThickBtn = ({ value }) => (
      <button
        type='button'
        className={`thick-btn-icon${Number(c.dividerThickness) === value ? ' active' : ''}`}
        onClick={() => setThickness(value)}
        title={`${value}px`}
      >
        {THICKNESS_ICON_MAP[value] ? (
          <img src={THICKNESS_ICON_MAP[value]} alt={`${value}px`} />
        ) : (
          <span className={`thickness-swatch t${value}`} />
        )}
      </button>
    );

    return (
      <>
        <div className='email-editor-header'>
          <h5 className='email-edit-text mt-4'>Edit Divider</h5>
          <IconButton
            className='close-icon'
            style={{ color: 'white', marginRight: '1rem' }}
            onClick={() => setSelectedCellId(null)}
          >
            <CloseIcon className='close-icon' style={{ color: 'white' }} />
          </IconButton>
        </div>

        <div className='email-editor-panel'>
          <div className='text-color-edit'>
            <span className='text-color-label'>Divider Color</span>
            <CustomColorPicker
              name='divcolor'
              value={c.dividerColor || '#7a6a55'}
              onChange={(e) => {
                handleUpdateCellWithHistory(c.id, { dividerColor: e.target.value });
              }}
            />
          </div>

          <div className='divider-thickness'>
            <span className='divider-thickness-label'>Thickness</span>
            <div className='thick-toggle' role='group'>
              <ThickBtn value={3} />
              <ThickBtn value={2} />
              <ThickBtn value={1} />
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderSocialPanel = () => {
    const c = selectedCell;
    const socialLinks = c.socialLinks || {};
    const order = c.socialOrder || DEFAULTS.social.order;

    const ALL = [
      ['LinkedIn', 'linkedin'],
      ['Facebook', 'facebook'],
      ['X/Twitter', 'twitter'],
      ['Instagram', 'instagram'],
      ['YouTube', 'youtube'],
    ];

    const update = (patch) => {
      handleUpdateCellWithHistory(c.id, patch);
    };

    const linkField = (label, key) => {
      const urlValue = socialLinks[key] || '';
      const hasError = socialUrlErrors[key];

      return (
        <div
          key={key}
          className='social-sort-row'
          draggable
          onDragStart={() => setDragKey(key)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            if (!dragKey || dragKey === key) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const isAfter = e.clientY > rect.top + rect.height / 2;
            const next = order.filter((k) => k !== dragKey);
            let at = next.indexOf(key);
            if (isAfter) at = at + 1;
            next.splice(at, 0, dragKey);
            setDragKey(null);
            update({ socialOrder: next });
          }}
          title='Drag to reorder'
        >
          <img src={sortIcon} alt='' className='sort-handle' draggable={false} />
          <div className='social-link-wrapper'>
            <div className='social-link-label'>{label}</div>
            <div className='social-link-input-wrapper'>
              <CustomTextField
                className={`social-link-field${hasError ? ' has-error' : ''}`}
                item={{
                  fieldName: '',
                  placeholder: '',
                  value: urlValue,
                  validations: [],
                  errors: {},
                }}
                type='text'
                hideAdornmentByDefault={false}
                handleInputChange={(e) => {
                  const val = e.value || '';
                  validateSocialUrl(key, val);
                  update({ socialLinks: { ...socialLinks, [key]: val } });
                }}
              />
              {!urlValue && !hasError && (
                <span className='social-link-placeholder'>Enter Link</span>
              )}
            </div>
            {hasError && <div className='social-url-error'>{socialUrlErrors[key]}</div>}
          </div>
        </div>
      );
    };

    return (
      <>
        <div className='email-editor-header'>
          <h5 className='email-edit-text mt-4'>Edit Social</h5>
          <IconButton
            className='close-icon'
            style={{ color: 'white', marginRight: '1rem' }}
            onClick={() => setSelectedCellId(null)}
          >
            <CloseIcon className='close-icon' style={{ color: 'white' }} />
          </IconButton>
        </div>

        <div className='email-editor-panel'>
          {order
            .map((k) => ALL.find(([, key]) => key === k))
            .filter(Boolean)
            .map(([label, key]) => linkField(label, key))}

          <hr className='horizontal-lines' />

          <div className='text-color-edit' style={{ marginTop: 12 }}>
            <span className='text-color-label'>Icon Color</span>
            <CustomColorPicker
              name='icol'
              value={c.iconColor || '#EED191'}
              onChange={(e) => update({ iconColor: e.target.value })}
            />
          </div>

          <div className='Icon-spacing-section' style={{ marginTop: 14 }}>
            <span className='Icon-spacing-label'>Icon Spacing</span>
            <CustomTextField
              item={{
                fieldName: 'Pixels',
                value:
                  c.iconSpacing === '' || c.iconSpacing === undefined || c.iconSpacing === null
                    ? ''
                    : c.iconSpacing,
                validations: [],
                errors: {},
              }}
              type='number'
              min={1}
              max={50}
              step={1}
              hideAdornmentByDefault={true}
              handleInputChange={(e) => {
                const val = e.value;
                if (val === '' || val === null) {
                  update({ iconSpacing: '' });
                  return;
                }
                const num = Number(val);
                if (Number.isNaN(num)) {
                  update({ iconSpacing: '' });
                  return;
                }
                const clamped = Math.min(50, Math.max(1, Math.floor(num)));
                update({ iconSpacing: clamped });
              }}
            />
          </div>

          <hr className='horizontal-lines' />

          <div className='alignment-buttons' style={{ marginTop: 14, paddingBottom: 60 }}>
            <span className='alignment-label'>Alignment</span>
            <div className='align-toggle' role='group'>
              {['left', 'center', 'right'].map((opt) => (
                <button
                  key={opt}
                  type='button'
                  className={`align-btn-icon${
                    (c.styles?.textAlign || 'center') === opt ? ' active' : ''
                  }`}
                  onClick={() => update({ styles: { ...(c.styles || {}), textAlign: opt } })}
                  title={opt}
                >
                  <img
                    src={
                      opt === 'left'
                        ? alignLeftIcon
                        : opt === 'center'
                        ? alignCenterIcon
                        : alignRightIcon
                    }
                    alt={opt}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderTextPanel = () => {
    const c = selectedCell;
    const styles = c.styles || {};

    return (
      <>
        <div className='email-editor-header'>
          <h5 className='email-edit-text mt-4'>Edit Text</h5>
          <IconButton
            className='close-icon'
            style={{ color: 'white', marginRight: '1rem' }}
            onClick={() => setSelectedCellId(null)}
          >
            <CloseIcon className='close-icon' style={{ color: 'white' }} />
          </IconButton>
        </div>

        <div className='email-editor-panel'>
          <div className='text-color-edit'>
            <span className='text-color-label'>Text Color</span>
            <CustomColorPicker
              name='txtcolor'
              value={styles.color || '#333333'}
              onChange={(e) => {
                handleUpdateCellWithHistory(c.id, {
                  styles: { ...styles, color: e.target.value },
                });
              }}
            />
          </div>

          <div className='text-color-edit' style={{ marginTop: 16 }}>
            <span className='text-color-label'>Background</span>
            <CustomColorPicker
              name='txtbg'
              value={styles.backgroundColor || 'transparent'}
              onChange={(e) => {
                handleUpdateCellWithHistory(c.id, {
                  styles: { ...styles, backgroundColor: e.target.value },
                });
              }}
            />
          </div>

          <hr className='horizontal-lines' />

          <div className='alignment-buttons' style={{ marginTop: 20 }}>
            <span className='alignment-label'>Alignment</span>
            <div className='align-toggle' role='group'>
              {['left', 'center', 'right'].map((opt) => (
                <button
                  key={opt}
                  type='button'
                  className={`align-btn-icon${
                    (styles.textAlign || 'left') === opt ? ' active' : ''
                  }`}
                  onClick={() => {
                    handleUpdateCellWithHistory(c.id, {
                      styles: { ...styles, textAlign: opt },
                    });
                  }}
                  title={opt}
                >
                  <img
                    src={
                      opt === 'left'
                        ? alignLeftIcon
                        : opt === 'center'
                        ? alignCenterIcon
                        : alignRightIcon
                    }
                    alt={opt}
                  />
                </button>
              ))}
            </div>
          </div>

          <p style={{ marginTop: 20, color: '#666', fontSize: 12 }}>
            Double-click the cell to edit text content directly.
          </p>
        </div>
      </>
    );
  };

  const renderImagePanel = () => {
    const c = selectedCell;

    return (
      <>
        <div className='email-editor-header'>
          <h5 className='email-edit-text mt-4'>Edit Image</h5>
          <IconButton
            className='close-icon'
            style={{ color: 'white', marginRight: '1rem' }}
            onClick={() => setSelectedCellId(null)}
          >
            <CloseIcon className='close-icon' style={{ color: 'white' }} />
          </IconButton>
        </div>

        <div className='email-editor-panel'>
          <div className='button-text-grid'>
            <CustomTextField
              className={`button-text-grid-url${imageUrlError ? ' has-error' : ''}`}
              item={{
                fieldName: 'Image URL',
                value: c.imageUrl || '',
                validations: [],
                errors: {},
              }}
              type='text'
              placeholder='https://…'
              hideAdornmentByDefault={true}
              handleInputChange={(e) => {
                const val = e.value || '';
                const isValid = !val || isValidUrl(val);
                setImageUrlError(isValid ? null : 'Invalid URL');
                handleUpdateCellWithHistory(c.id, { imageUrl: val });
              }}
            />
            {imageUrlError && (
              <div className='social-url-error' style={{ marginTop: 4 }}>
                {imageUrlError}
              </div>
            )}
          </div>

          <div className='button-text-grid' style={{ marginTop: 12 }}>
            <CustomTextField
              className={`button-text-grid-url${redirectUrlError ? ' has-error' : ''}`}
              item={{
                fieldName: 'Redirect URL',
                value: c.redirectUrl || '',
                validations: [],
                errors: {},
              }}
              type='text'
              placeholder='https://…'
              hideAdornmentByDefault={true}
              handleInputChange={(e) => {
                const val = e.value || '';
                const isValid = !val || isValidUrl(val);
                setRedirectUrlError(isValid ? null : 'Invalid URL');
                handleUpdateCellWithHistory(c.id, { redirectUrl: val });
              }}
            />
            {redirectUrlError && (
              <div className='social-url-error' style={{ marginTop: 4 }}>
                {redirectUrlError}
              </div>
            )}
          </div>
{/* 
          <div className='button-text-grid' style={{ marginTop: 12 }}>
            <CustomTextField
              className='button-text-grid-field'
              item={{
                fieldName: 'Alt Text',
                value: c.imageAlt || '',
                validations: [],
                errors: {},
              }}
              type='text'
              placeholder='Image description'
              hideAdornmentByDefault={true}
              handleInputChange={(e) => {
                handleUpdateCellWithHistory(c.id, { imageAlt: e.value || '' });
              }}
            />
          </div> */}
       
        </div>
      </>
    );
  };

  const renderTablePanel = () => {
    const c = selectedCell;
    if (!c || c.type !== CELL_TYPES.TABLE) return null;
    const model = c.tableModel;
    if (!model) return renderDefaultPanel();

    return (
      <TableRightPanel
        model={model}
        onChange={(newModel) => {
          handleUpdateCellWithHistory(c.id, { tableModel: newModel });
        }}
        selectedCellId={null}
        onClose={() => setSelectedCellId(null)}
        onRemoveTable={() => {
          const newSchema = removeCell(schema, c.id);
          updateSchemaWithHistory(newSchema);
          setSelectedCellId(null);
        }}
      />
    );
  };

  const renderLoopPanel = () => {
    const c = selectedCell;
    if (!c || c.type !== CELL_TYPES.LOOP) return null;
    const conditions = c.loopConditions || [];
    const ifRangeCount = conditions.filter((cd) => cd.type === 'if' || cd.type === 'range').length;
    const endCount = conditions.filter((cd) => cd.type === 'end').length;

    const addCondition = (type) => {
      if (type === 'end' || type === 'range') {
        // Create a new separate LOOP cell for {{end}} and {{range}}
        const newCell = createCell(CELL_TYPES.LOOP);
        newCell.x = c.x;
        newCell.y = c.y + c.height + 10;
        newCell.width = c.width;
        newCell.height = 40;
        newCell.loopConditions = [{ id: Math.random().toString(36).slice(2, 10), type, value: '' }];
        const newSchema = addCell(schema, newCell);
        updateSchemaWithHistory(newSchema);
        setSelectedCellId(newCell.id);
      } else {
        // For {{if}}, add to current cell's conditions
        const updated = [...conditions, { id: Math.random().toString(36).slice(2, 10), type, value: '' }];
        handleUpdateCellWithHistory(c.id, { loopConditions: updated });
      }
    };

    const removeCondition = (idx) => {
      const updated = conditions.filter((_, i) => i !== idx);
      handleUpdateCellWithHistory(c.id, { loopConditions: updated });
    };

    const updateConditionValue = (idx, value) => {
      const updated = [...conditions];
      updated[idx] = { ...updated[idx], value };
      handleUpdateCellWithHistory(c.id, { loopConditions: updated });
    };

    return (
      <>
        <div className='email-editor-header'>
          <h5 className='email-edit-text mt-4'>Edit Loop</h5>
          <IconButton
            className='close-icon'
            style={{ color: 'white', marginRight: '1rem' }}
            onClick={() => setSelectedCellId(null)}
          >
            <CloseIcon className='close-icon' style={{ color: 'white' }} />
          </IconButton>
        </div>

        <div className='email-editor-panel' style={{ paddingBottom: 60 }}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#333', marginBottom: 8 }}>Add Conditions</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type='button' onClick={() => addCondition('if')}
                style={{ flex: 1, padding: '6px 10px', fontSize: 12, fontWeight: 600, border: '1px solid #6c63ff', borderRadius: 6, background: '#f5f3ff', color: '#6c63ff', cursor: 'pointer' }}>
                + {'{{if.}}'}
              </button>
              <button type='button' onClick={() => addCondition('range')}
                style={{ flex: 1, padding: '6px 10px', fontSize: 12, fontWeight: 600, border: '1px solid #6c63ff', borderRadius: 6, background: '#f5f3ff', color: '#6c63ff', cursor: 'pointer' }}>
                + {'{{range.}}'}
              </button>
              <button type='button' onClick={() => addCondition('end')}
                style={{ flex: 1, padding: '6px 10px', fontSize: 12, fontWeight: 600, border: '1px solid #dc2626', borderRadius: 6, background: '#fef2f2', color: '#dc2626', cursor: 'pointer' }}>
                + {'{{end}}'}
              </button>
            </div>
          </div>

          <hr className='horizontal-lines' />

          <div style={{ fontSize: 12, fontWeight: 600, color: '#333', marginBottom: 8, marginTop: 12 }}>
            Conditions ({conditions.length})
          </div>

          {conditions.length === 0 && (
            <div style={{ color: '#999', fontSize: 12, fontStyle: 'italic', marginBottom: 12 }}>
              No conditions added. Click the buttons above to add.
            </div>
          )}

          {conditions.map((cond, idx) => (
            <div key={cond.id || idx} style={{
              display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6,
              padding: '6px 8px', borderRadius: 6,
              background: cond.type === 'end' ? '#fef2f2' : '#f5f3ff',
              border: `1px solid ${cond.type === 'end' ? '#fecaca' : '#e9d5ff'}`,
            }}>
              {cond.type === 'end' ? (
                <span style={{ flex: 1, fontSize: 12, fontFamily: 'monospace', fontWeight: 600, color: '#dc2626' }}>{'{{end}}'}</span>
              ) : (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <span style={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 600, color: '#6c63ff', whiteSpace: 'nowrap' }}>
                    {`{{${cond.type} .`}
                  </span>
                  <input
                    type='text'
                    value={cond.value || ''}
                    placeholder='variable'
                    onChange={(e) => updateConditionValue(idx, e.target.value)}
                    style={{
                      flex: 1, fontSize: 12, fontFamily: 'monospace', padding: '3px 6px',
                      border: '1px solid #d1d5db', borderRadius: 4, outline: 'none', minWidth: 0,
                    }}
                  />
                  <span style={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 600, color: '#6c63ff' }}>{'}}'}</span>
                </div>
              )}
              <button type='button' onClick={() => removeCondition(idx)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 16, padding: '0 4px', lineHeight: 1 }}>
                ×
              </button>
            </div>
          ))}

          {ifRangeCount > 0 && (
            <div style={{
              marginTop: 8, padding: '6px 10px', borderRadius: 6, fontSize: 11,
              background: endCount >= ifRangeCount ? '#f0fdf4' : '#fef3c7',
              border: `1px solid ${endCount >= ifRangeCount ? '#bbf7d0' : '#fde68a'}`,
              color: endCount >= ifRangeCount ? '#16a34a' : '#92400e',
            }}>
              {endCount >= ifRangeCount
                ? `✓ ${endCount} {{end}} for ${ifRangeCount} opening condition${ifRangeCount > 1 ? 's' : ''}`
                : `⚠ Need ${ifRangeCount - endCount} more {{end}} (${ifRangeCount} opening, ${endCount} closing)`}
            </div>
          )}
        </div>
      </>
    );
  };

  const renderDefaultPanel = () => (
    <>
      <div className='tools-header d-flex mb-3'>
        <div
          className={`tool-toggle flex-fill text-center ${
            activeTab === 'components' ? 'active' : ''
          } ${isReadOnly ? 'disabled' : ''}`}
          onClick={() => !isReadOnly && setActiveTab('components')}
        >
          <img src={componentsIcon} className='tool-icon me-2' alt='Components' /> Components
        </div>
        <div
          className={`tool-toggle flex-fill text-center ${activeTab === 'body' ? 'active' : ''}`}
          onClick={() => !isReadOnly && setActiveTab('body')}
        >
          <img src={bodyIcon} className='tool-icon body-icon' alt='Body' /> Body
        </div>
      </div>

      {activeTab === 'components' ? (
        <div className='components-panel'>
          <div
            className={`component-email-item ${isReadOnly ? 'disabled' : ''}`}
            draggable={!isReadOnly}
            onDragStart={(e) => onDragStart(e, 'text')}
            onClick={() => quickInsert(CELL_TYPES.TEXT)}
            title='Text Cell / Box — click to insert'
          >
            <img className='comp-thumb' src={textCellCompImg} alt='Text cell component' />
          </div>

          <div
            className={`component-email-item ${isReadOnly ? 'disabled' : ''}`}
            draggable={!isReadOnly}
            onDragStart={(e) => onDragStart(e, 'button')}
            onClick={() => quickInsert(CELL_TYPES.BUTTON)}
            title='Button — click to insert'
          >
            <img className='comp-thumb' src={buttonCompImg} alt='Button component' />
          </div>

          <div
            className={`component-email-item ${isReadOnly ? 'disabled' : ''}`}
            draggable={!isReadOnly}
            onDragStart={(e) => onDragStart(e, 'divider')}
            onClick={() => quickInsert(CELL_TYPES.DIVIDER)}
            title='Divider — click to insert'
          >
            <img className='comp-thumb' src={dividerCompImg} alt='Divider component' />
          </div>

          <div
            className={`component-email-item ${isReadOnly ? 'disabled' : ''}`}
            draggable={!isReadOnly}
            onDragStart={(e) => onDragStart(e, 'social')}
            onClick={() => quickInsert(CELL_TYPES.SOCIAL)}
            title='Social — click to insert'
          >
            <img className='comp-thumb' src={socialCompImg} alt='Social component' />
          </div>

          <div
            className={`component-email-item ${isReadOnly ? 'disabled' : ''}`}
            draggable={!isReadOnly}
            onDragStart={(e) => onDragStart(e, 'image')}
            onClick={() => quickInsert(CELL_TYPES.IMAGE)}
            title='Image — click to insert'
          >
            <img className='comp-thumb' src={imageCompImg} alt='Image component' />
          </div>

          <div
            className={`component-email-item ${isReadOnly ? 'disabled' : ''}`}
            draggable={!isReadOnly}
            onDragStart={(e) => onDragStart(e, 'loop')}
            onClick={() => quickInsert(CELL_TYPES.LOOP)}
            title='Loop — click to insert'
          >
            <img className='comp-thumb' src={loopCompImg} alt='Loop component' />
          </div>

          <div
            className={`component-email-item ${isReadOnly ? 'disabled' : ''}`}
            onClick={() => !isReadOnly && setShowTableGridPicker(true)}
            title='Table — click to insert'
            style={{ position: 'relative' }}
          >
            <img className='comp-thumb' src={tableCompImg} alt='Table component' />
            {showTableGridPicker && (
              <>
                <div
                  className='etbl-picker-backdrop'
                  onClick={(e) => { e.stopPropagation(); setShowTableGridPicker(false); }}
                />
                <div className='etbl-picker-popover' onClick={(e) => e.stopPropagation()}>
                  <TableGridSelector
                    onSelect={(r, c) => handleInsertTable(r, c)}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className='email-body-panel'>
          <div className='text-color-body-section mt-2'>
            <span className='bg-label'>Background Color</span>
            <CustomColorPicker
              name='backgroundColor'
              value={schema.bgColor || '#ffffff'}
              onChange={(e) => handleBgColorChange(e.target.value)}
            />
          </div>

          <hr className='horizontal-line' />

          <div className='variable-keywords-section'>
            <div className='variable-title'>Variable Keywords</div>
            <div className='variable-description'>
              Add variables by typing them inside <code>[ ]</code>, e.g. <code>[name]</code>.
            </div>
          </div>
          <div className='view-variable-list-mt-2 save'>
            <Button
              variant='outlined'
              color='secondary'
              size='small'
              onClick={() => setShowVariableList(true)}
            >
              View List
            </Button>
          </div>

          <EmailVariableKeywordsModal
            isOpen={showVariableList}
            onClose={() => setShowVariableList(false)}
            responseKey='listEmailTemplateVariables'
          />
        </div>
      )}
    </>
  );

  const VariableDropdown = ({ position, variables, onSelect }) => {
    const [searchText, setSearchText] = useState('');
    if (!variables || !variables.length) return null;
    const filtered = variables.filter((v) =>
      v?.keyword?.toLowerCase().includes(searchText?.toLowerCase()),
    );
    const dropdownHeight = 250;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    let adjustedTop = position?.y || 0;
    let adjustedLeft = position?.x || 0;

    if (adjustedTop + dropdownHeight > viewportHeight) {
      adjustedTop = Math.max(0, position?.y - dropdownHeight - 10);
    }

    const dropdownWidth = 212;
    if (adjustedLeft + dropdownWidth > viewportWidth) {
      adjustedLeft = Math.max(0, viewportWidth - dropdownWidth - 10);
    }

    return (
      <div
        className='variable-dropdown'
        style={{
          top: adjustedTop,
          left: adjustedLeft,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className='variable-header'>Variables</div>
        <div className='variable-items-container'>
          <div className='variable-search mb-2' style={{ padding: '10px 8px' }}>
            <TextField
              value={searchText}
              autoFocus
              fullWidth
              placeholder=''
              onChange={(e) => setSearchText(e.target.value)}
              className='variable-search-input'
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Escape') e.stopPropagation();
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          {filtered.length === 0 ? (
            <div className='variable-item no-results'>No variables found</div>
          ) : (
            filtered.map((variable, index) => (
              <div key={index} className='variable-item' onClick={() => onSelect(variable)}>
                {variable.keyword}
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  const handleCellInput = useCallback(
    (e, cellId) => {
      const el = e.currentTarget;
      const text = el.innerText;
      currentContentRef.current = el.innerHTML;

      if (e.nativeEvent.data === '[') {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const rect = range.getBoundingClientRect();

          setVariableDropdownPosition({
            x: rect.left,
            y: rect.bottom + 5,
          });
          setShowVariableDropdown(true);
          setSelectedCellForVar(cellId);

          if (variables.length === 0) {
            getEmailTemplateVariables('variables').then((response) => {
              setVariables(response?.data?.listEmailTemplateVariables || []);
            });
          }
        }
      }

      if (e.nativeEvent.data === ']' || !text.includes('[')) {
        setShowVariableDropdown(false);
      }
    },
    [variables],
  );

  const handleLoopVarTrigger = useCallback(
    (cellId, condIdx, inputEl) => {
      if (!inputEl) return;
      const rect = inputEl.getBoundingClientRect();
      setVariableDropdownPosition({ x: rect.left, y: rect.bottom + 5 });
      setShowVariableDropdown(true);
      setSelectedCellForVar(cellId);
      loopVarCondIdxRef.current = condIdx;

      if (variables.length === 0) {
        getEmailTemplateVariables('variables').then((response) => {
          setVariables(response?.data?.listEmailTemplateVariables || []);
        });
      }
    },
    [variables],
  );

  const handleVariableSelect = (variable) => {
    if (!selectedCellForVar) return;

    const cell = getCellById(schema, selectedCellForVar);
    if (!cell) return;

    // Handle LOOP cell — insert variable into the specific condition's value
    if (cell.type === CELL_TYPES.LOOP && loopVarCondIdxRef.current != null) {
      const condIdx = loopVarCondIdxRef.current;
      const conditions = [...(cell.loopConditions || [])];
      if (conditions[condIdx]) {
        let val = conditions[condIdx].value || '';
        const bracketIdx = val.lastIndexOf('[');
        if (bracketIdx !== -1) {
          val = val.substring(0, bracketIdx) + variable.keyword + val.substring(bracketIdx + 1);
        } else {
          val += variable.keyword;
        }
        conditions[condIdx] = { ...conditions[condIdx], value: val };
        handleUpdateCellWithHistory(selectedCellForVar, { loopConditions: conditions });
      }
      loopVarCondIdxRef.current = null;
      setShowVariableDropdown(false);
      return;
    }

    let content = currentContentRef.current || cell.content || '';
    const lastBracketIndex = content.lastIndexOf('[');
    if (lastBracketIndex !== -1) {
      const newContent =
        content.substring(0, lastBracketIndex) +
        variable.keyword +
        content.substring(lastBracketIndex + 1);
      handleUpdateCellWithHistory(selectedCellForVar, { content: newContent });
      currentContentRef.current = '';
    }

    setShowVariableDropdown(false);
  };

  return (
    <div className='email-template d-flex' style={{ margin: '0px -30px 0px 0px' }}>
      {showVariableDropdown && (
        <VariableDropdown
          ref={variableDropdownRef}
          position={variableDropdownPosition}
          variables={variables}
          onSelect={handleVariableSelect}
        />
      )}
      <div className='flex-grow-1 me-3'>
        <div
          className='email-template-canvas cell-editor-canvas'
          style={{ position: 'relative', padding: 0 }}
        >
          <CellToolbar
            onAddCell={handleAddCell}
            onDeleteCell={handleDeleteCell}
            onDuplicateCell={handleDuplicateCell}
            onBringToFront={handleBringToFront}
            onSendToBack={handleSendToBack}
            onUndo={undo}
            onRedo={redo}
            canUndo={canUndo}
            canRedo={canRedo}
            selectedCell={selectedCell}
            onUpdateSelectedCellContent={(content) => {
              if (selectedCell && selectedCell.id) {
                handleUpdateCellWithHistory(selectedCell.id, { content });
              }
            }}
            isReadOnly={isReadOnly}
          />

          <div
            ref={scrollContainerRef}
            className='cell-canvas-scroll-container'
            style={{
              maxHeight: '512px',
              overflowY: 'auto',
              overflowX: 'auto',
              border: '1px solid #e6e6e6',
              borderRadius: '4px',
            }}
          >
            <CellCanvas
              schema={schema}
              selectedCellId={selectedCellId}
              onSelectCell={setSelectedCellId}
              onUpdateCell={handleUpdateCell}
              onUpdateCellComplete={handleUpdateCellComplete}
              onContentChange={handleContentChange}
              isReadOnly={isReadOnly}
              onCellInput={handleCellInput}
              onLoopVarTrigger={handleLoopVarTrigger}
            />
          </div>
        </div>

        {canvasFooter && (
          <div
            className='canvas-footer mb-2'
            style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: '15px' }}
          >
            {canvasFooter}
          </div>
        )}
      </div>

      <div className='component-container' style={{ width: 300 }}>
        {renderRightPanel()}
      </div>
    </div>
  );
};

export default CellEditor;
